/**
 * Playlist cycling state for Present Mode (S13). Advances through
 * `playlistSequence` on a real-second timer and reports which dashboard is
 * currently shown vs. on deck (inside the pre-warm lead window) so the
 * caller can mark the on-deck dashboard eligible in the S3 scheduler and
 * mount `DashboardPrewarmMount` for it ahead of the switch.
 *
 * `dashboard` is always the ENTRY dashboard (the one Present Mode was opened
 * on) — its `playlist` field defines the sequence, which stays stable across
 * cycles even though the visually-shown dashboard changes.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Dashboard } from '../types/dashboardTypes';
import { onDeckSequenceIndex, playlistSequence } from '../services/playlistSequence';
import { usePresentRequestStore } from '../store/presentRequestStore';

const TICK_MS = 1000;

export type PresentPlaylistState = {
  shownDashboard: Dashboard | null;
  onDeckDashboard: Dashboard | null;
  index: number;
  total: number;
};

export function usePresentPlaylist(
  dashboard: Dashboard | null,
  dashboards: Dashboard[],
  active: boolean,
): PresentPlaylistState {
  const [index, setIndex] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  const knownIds = useMemo(() => new Set(dashboards.map((d) => d.id)), [dashboards]);
  const sequence = useMemo(
    () => (active && dashboard ? playlistSequence(dashboard, knownIds) : []),
    [active, dashboard, knownIds],
  );
  const secondsPerDashboard = dashboard?.playlist?.secondsPerDashboard ?? 0;

  // A fresh presentation (new entry dashboard, or presenting just started)
  // always begins at the entry dashboard's own position in the sequence.
  useEffect(() => {
    setIndex(0);
    setElapsedMs(0);
  }, [active, dashboard?.id]);

  useEffect(() => {
    if (!active || sequence.length < 2 || secondsPerDashboard <= 0) return;
    const timer = setInterval(() => {
      setElapsedMs((prev) => {
        const next = prev + TICK_MS;
        if (next < secondsPerDashboard * 1000) return next;
        setIndex((i) => (i + 1) % sequence.length);
        return 0;
      });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [active, sequence.length, secondsPerDashboard]);

  // Publish the live cycle position for PresentMode's countdown ring. Derived
  // from the SAME tick that advances the playlist above — no second timer; a
  // cadence of 0 (nothing to advance to) publishes an idle cycle so the ring
  // renders nothing rather than a countdown that never fires.
  const cycleMs = active && sequence.length > 1 && secondsPerDashboard > 0
    ? secondsPerDashboard * 1000
    : 0;
  const setCycle = usePresentRequestStore((s) => s.setCycle);
  useEffect(() => {
    // Only the ACTIVE presenter publishes — a second, non-presenting instance
    // of this hook must not blank the ring of the one on screen.
    if (!active) return;
    setCycle({ elapsedMs: cycleMs > 0 ? elapsedMs : 0, cycleMs });
  }, [active, elapsedMs, cycleMs, setCycle]);
  useEffect(() => {
    if (!active) return;
    return () => setCycle({ elapsedMs: 0, cycleMs: 0 });
  }, [active, setCycle]);

  const shownId = sequence[index] ?? dashboard?.id ?? null;
  const shownDashboard = (shownId ? dashboards.find((d) => d.id === shownId) : undefined) ?? dashboard;

  const onDeckIdx = active
    ? onDeckSequenceIndex(sequence, index, secondsPerDashboard, elapsedMs)
    : null;
  const onDeckDashboard =
    onDeckIdx !== null ? (dashboards.find((d) => d.id === sequence[onDeckIdx]) ?? null) : null;

  return { shownDashboard, onDeckDashboard, index, total: sequence.length };
}
